<template>
  <div v-if="!ready || visibleButtons.length === 0" />
  <component :is="keyToComponents[visibleButtons[0].key]" v-else-if="visibleButtons.length === 1" />
  <HamburgerButton v-else :open.sync="menuOpen" class="icon-container" style="margin: -6px 2px 0 0">
    <Teleport to="body">
      <transition name="slide">
        <div v-if="menuOpen" class="slideout-container" @click="menuOpen = false">
          <div class="menu-container" @click="$emit('click')">
            <!-- other buttons -->
            <component :is="keyToComponents[key]" v-for="{ key } in visibleButtons" :key="key" />
          </div>
        </div>
      </transition>
    </Teleport>
  </HamburgerButton>
</template>

<script lang="ts">
import Vue, { VueConstructor } from 'vue'
import Component from 'vue-class-component'

import { useNavButtonsStore } from '@/store/public/navButtons'
import MenuButtonBubbles from '@/components/public/MenuButtons/MenuButtonBubbles.vue'
import MenuButtonMap from '@/components/public/MenuButtons/MenuButtonMap.vue'
import MenuButtonNestedBubbles from '@/components/public/MenuButtons/MenuButtonNestedBubbles.vue'
import MenuButtonSunburst from '@/components/public/MenuButtons/MenuButtonSunburst.vue'
import MenuButtonXYBubbles from '@/components/public/MenuButtons/MenuButtonXYBubbles.vue'
import MenuButtonSearch from '@/components/public/MenuButtons/MenuButtonSearch.vue'
import MenuButtonFaq from '@/components/public/MenuButtons/MenuButtonFaq.vue'
import MenuButtonDownload from '@/components/public/MenuButtons/MenuButtonDownload.vue'
import MenuButtonFilterByCategory from '@/components/public/MenuButtons/MenuButtonFilterByCategory.vue'
import MenuButtonReset from '@/components/public/MenuButtons/MenuButtonReset.vue'
import { NavButtonKeys } from '@/types'

import HamburgerButton from '@/components/public/mobile/HamburgerButton.vue'
import Teleport from 'vue2-teleport' // TODO: Remove this import and component when migrating to vue3

const components = {
  Teleport,
  HamburgerButton,
}

const props = {
  isMobile: {
    type: Boolean,
    default: true,
  },
}

@Component({ components })
export default class MobileMenu extends Vue.extend({ props }) {
  menuOpen = false
  navButtonsStore = useNavButtonsStore()

  get ready() {
    return !!this.$typedStore.activeVisualisation.visualisationStore.app.config.navButtons
  }

  get staticBtns() {
    return this.navButtonsStore.staticBtns.filter((b) => b.key === NavButtonKeys.RESET)
  }

  get standaloneBtns() {
    return this.navButtonsStore.standaloneBtns
  }

  get menuItemBtns() {
    return this.navButtonsStore.menuItemBtns
  }

  get visualisationBtns() {
    return this.navButtonsStore.visualisationBtns.length > 1
      ? this.navButtonsStore.visualisationBtns
      : []
  }

  get visibleButtons() {
    return [
      ...this.staticBtns,
      ...this.standaloneBtns,
      ...this.menuItemBtns,
      ...this.visualisationBtns,
    ]
  }

  get keyToComponents(): Record<string, VueConstructor> {
    return {
      [NavButtonKeys.RESET]: MenuButtonReset,
      [NavButtonKeys.SEARCH]: MenuButtonSearch,
      [NavButtonKeys.INFO]: MenuButtonFaq,
      [NavButtonKeys.DOWNLOAD]: MenuButtonDownload,
      [NavButtonKeys.FILTER]: MenuButtonFilterByCategory,
      [NavButtonKeys.BUBBLES]: MenuButtonBubbles,
      [NavButtonKeys.MAP]: MenuButtonMap,
      [NavButtonKeys.NESTED_BUBBLES]: MenuButtonNestedBubbles,
      [NavButtonKeys.SUNBURST]: MenuButtonSunburst,
      [NavButtonKeys.XY_BUBBLES]: MenuButtonXYBubbles,
    }
  }

  get disableMobileHamburger() {
    return this.$typedStore.primary.app.config.appConfig.disableMobileHamburger
  }

  goBack() {
    window.history.back()
  }
}
</script>

<style lang="scss" scoped>
.slideout-container {
  z-index: 4;
  position: fixed;
  top: 0;
  height: calc(100% - 44px);
  width: 100%;
  margin-top: 44px;
  transition: transform 0.2s;
  backdrop-filter: blur(1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  @media (orientation: landscape) {
    left: 0;
    &.slide-enter,
    &.slide-leave-to {
      transform: translateX(-100%);
    }
  }
  @media (orientation: portrait) {
    right: 0;
    &.slide-enter,
    &.slide-leave-to {
      transform: translateX(100%);
    }
  }
  &.slide-enter,
  &.slide-leave-to {
    .menu-overlay {
      opacity: 0;
    }
  }
  .menu-overlay {
    z-index: -1;
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    transition: opacity 0.8s;
    background: rgba(1, 1, 1, 0.3);
  }
  .menu-container {
    background: white;
    padding: 1em;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-around;
    width: fit-content;
    position: absolute;
    $border-radius: 2px;
    gap: 5px;
    @media (orientation: portrait) {
      right: 0;
      box-shadow: -2px 0 15px 2px #000;
      border-radius: $border-radius 0 0 $border-radius;
    }
    @media (orientation: landscape) {
      left: 0;
      box-shadow: 2px 0 15px 2px #000;
      border-radius: 0 $border-radius $border-radius 0;
    }
    a.nav-link {
      color: inherit !important;
    }
  }
}
</style>
